<template>
  <div class="sf" :class="{ 'is-busy': isSaving, 'is-dirty': dirty }">
    <div v-if="isSaving" class="sf-progress" aria-hidden="true"><i /></div>

    <div class="sf-main">
    <header class="sf-top">
      <div class="sf-hero">
        <p class="sf-kicker">Shopfront</p>
        <h1 class="sf-title">{{ shop.name || "Your shop" }}</h1>
        <p class="sf-sub">
          This is what customers see when they book online.
        </p>
      </div>
    </header>

    <ValidationObserver v-slot="{ handleSubmit }">
      <form
        id="shop-settings-form"
        class="sf-body"
        @submit.prevent="handleSubmit(submit)"
      >
        <section class="sf-card">
          <div class="sf-card-head">
            <div>
              <h2>Your shop</h2>
              <p class="sf-lead">
                Name, logo and colour customers see first.
              </p>
            </div>
            <button
              class="sf-text-btn"
              type="button"
              :disabled="isSaving"
              @click="useBusinessDetails"
            >
              Copy from Business details
            </button>
          </div>
          <div class="sf-identity">
            <shop-logo-input
              v-model="model.logoDownloadUrl"
              @error="onLogoError"
            />
            <div class="sf-identity-fields">
              <label class="sf-field">
                Shop name
                <ValidationProvider rules="required|min:2" v-slot="{ errors }">
                  <fg-input
                    class="mb-0"
                    v-model="model.name"
                    placeholder="The name over the door"
                    :error="errors[0]"
                  />
                </ValidationProvider>
              </label>
              <div class="sf-field">
                <span>Colour</span>
                <p class="sf-hint">Buttons on the booking page use this.</p>
                <v-swatches
                  v-model="model.colorTheme"
                  show-fallback
                  fallback-input-type="color"
                  popover-x="left"
                />
              </div>
            </div>
          </div>
        </section>

        <section class="sf-card">
          <div class="sf-card-head">
            <div>
              <h2>Find you</h2>
              <p class="sf-lead">
                How people get in touch and find the shop.
              </p>
            </div>
          </div>
          <div class="sf-stack">
            <div class="sf-pair">
              <label class="sf-field">
                Contact email
                <ValidationProvider rules="email" v-slot="{ errors }">
                  <fg-input
                    class="mb-0"
                    type="email"
                    v-model="model.contactEmail"
                    placeholder="hello@shop.ie"
                    :error="errors[0]"
                  />
                </ValidationProvider>
              </label>
              <label class="sf-field">
                Contact number
                <fg-input
                  class="mb-0"
                  type="tel"
                  v-model="model.contactNumber"
                  placeholder="01 234 5678"
                />
              </label>
            </div>
            <label class="sf-field">
              Address line 1
              <ValidationProvider rules="required" v-slot="{ errors }">
                <fg-input
                  class="mb-0"
                  v-model="model.address.line1"
                  :placeholder="$t('message.addressLine1Placeholder')"
                  :error="errors[0]"
                />
              </ValidationProvider>
            </label>
            <label class="sf-field">
              Address line 2
              <fg-input
                class="mb-0"
                v-model="model.address.line2"
                :placeholder="$t('message.addressLine2Placeholder')"
              />
            </label>
            <div class="sf-pair">
              <label class="sf-field">
                Town / district
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <fg-input
                    class="mb-0"
                    v-model="model.address.district"
                    :placeholder="$t('message.addressDistrictPlaceholder')"
                    :error="errors[0]"
                  />
                </ValidationProvider>
              </label>
              <label class="sf-field">
                County / region
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <fg-input
                    class="mb-0"
                    v-model="model.address.region"
                    :placeholder="$t('message.addressRegionPlaceholder')"
                    :error="errors[0]"
                  />
                </ValidationProvider>
              </label>
            </div>
            <div class="sf-pair">
              <label class="sf-field">
                Country
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <fg-input
                    class="mb-0"
                    v-model="model.address.country"
                    :placeholder="$t('message.addressCountryPlaceholder')"
                    :error="errors[0]"
                  />
                </ValidationProvider>
              </label>
              <label class="sf-field">
                Eircode / postcode
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <fg-input
                    class="mb-0"
                    v-model="model.address.postcode"
                    :placeholder="$t('message.addressPostcodePlaceholder')"
                    :error="errors[0]"
                  />
                </ValidationProvider>
              </label>
            </div>
            <label class="sf-field">
              Map link
              <p class="sf-hint">
                Paste a Google Maps link so they can get directions.
              </p>
              <fg-input
                class="mb-0"
                v-model="model.googleMapsUrl"
                placeholder="https://maps.google.com/…"
              />
            </label>
            <label class="sf-field">
              Website
              <fg-input
                class="mb-0"
                v-model="model.websiteUrl"
                placeholder="https://"
              />
            </label>
            <div class="sf-field">
              <span>Your booking link</span>
              <p class="sf-hint">Share this so people can book with you.</p>
              <div class="sf-url">
                <a :href="storefrontUrl" target="_blank" rel="noopener">{{
                  storefrontUrl
                }}</a>
                <div class="sf-url-actions">
                  <button
                    class="sf-chip sf-chip-sm"
                    type="button"
                    @click="copyUrl"
                  >
                    {{ copied ? "Copied" : "Copy" }}
                  </button>
                  <a
                    v-if="storefrontUrl"
                    class="sf-chip sf-chip-sm"
                    :href="storefrontUrl"
                    target="_blank"
                    rel="noopener"
                  >
                    Open booking page
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="sf-card">
          <div class="sf-card-head">
            <div>
              <h2>Hours</h2>
              <p class="sf-lead">
                When you take bookings. Tick a day to open it. A note is
                optional — use it for things like “Walk-ins only”.
              </p>
            </div>
          </div>
          <div class="sf-hours">
            <div
              v-for="day in days"
              :key="day"
              class="sf-day"
              :class="{ 'is-closed': !model.daysOpen[day] }"
            >
              <label class="sf-day-name">
                <input
                  type="checkbox"
                  v-model="model.daysOpen[day]"
                />
                <span>{{ dayLabel(day) }}</span>
                <em class="sf-day-state">{{
                  model.daysOpen[day] ? "Open" : "Closed"
                }}</em>
              </label>
              <div class="sf-day-times">
                <vue-ctk-date-time-picker
                  :id="'opening-time__' + day"
                  label="From"
                  v-model="model.openingTimes[day]"
                  :format="TIME_FORMAT_CODE"
                  :formatted="TIME_FORMAT_READABLE"
                  :only-time="true"
                  :right="true"
                  :minute-interval="5"
                  required
                  no-clear-button
                  no-keyboard
                  :disabled="!model.daysOpen[day]"
                />
                <vue-ctk-date-time-picker
                  :id="'closing-time__' + day"
                  label="To"
                  v-model="model.closingTimes[day]"
                  :format="TIME_FORMAT_CODE"
                  :formatted="TIME_FORMAT_READABLE"
                  :only-time="true"
                  :right="true"
                  :minute-interval="5"
                  required
                  no-clear-button
                  no-keyboard
                  :disabled="!model.daysOpen[day]"
                />
              </div>
              <fg-input
                class="mb-0 sf-day-note"
                v-model="model.openingHoursNotes[day]"
                placeholder="Optional note"
                maxlength="80"
              />
            </div>
          </div>
        </section>

        <section class="sf-card">
          <div class="sf-card-head">
            <div>
              <h2>Booking</h2>
              <p class="sf-lead">
                Words and choices on the booking page.
              </p>
            </div>
          </div>
          <div class="sf-stack">
            <p class="sf-hint sf-hint-block">
              These headings appear on each step while someone books.
            </p>
            <div class="sf-pair">
              <label class="sf-field">
                Services heading
                <fg-input class="mb-0" v-model="model.serviceSelectionTitle" />
              </label>
              <label class="sf-field">
                Packages heading
                <fg-input
                  class="mb-0"
                  v-model="model.servicePackageSelectionTitle"
                />
              </label>
            </div>
            <div class="sf-pair">
              <label class="sf-field">
                People heading
                <fg-input class="mb-0" v-model="model.employeeSelectionTitle" />
              </label>
              <label class="sf-field">
                Times heading
                <fg-input class="mb-0" v-model="model.timeSelectionTitle" />
              </label>
            </div>
            <div class="sf-field">
              <span>Who they can book with</span>
              <p class="sf-hint">
                “Anyone free” is the first person available at that time.
              </p>
              <div
                class="sf-seg"
                role="radiogroup"
                aria-label="Who they can book with"
              >
                <button
                  v-for="option in AnyModeOptions"
                  :key="option.value"
                  type="button"
                  class="sf-seg-btn"
                  :class="{ 'is-on': model.resourceAnyMode === option.value }"
                  @click="model.resourceAnyMode = option.value"
                >
                  {{ option.label }}
                </button>
              </div>
            </div>
            <div class="sf-field">
              <span>Ask for their address</span>
              <p class="sf-hint">
                Turn on if you go to them, or need a postal address.
              </p>
              <div
                class="sf-seg"
                role="radiogroup"
                aria-label="Ask for their address"
              >
                <button
                  type="button"
                  class="sf-seg-btn"
                  :class="{ 'is-on': model.hasCustomerAddressField === true }"
                  @click="model.hasCustomerAddressField = true"
                >
                  Yes
                </button>
                <button
                  type="button"
                  class="sf-seg-btn"
                  :class="{ 'is-on': model.hasCustomerAddressField === false }"
                  @click="model.hasCustomerAddressField = false"
                >
                  No
                </button>
              </div>
            </div>
            <label class="sf-field sf-field-narrow">
              Extra fee for paying online
              <p class="sf-hint">
                Added on top of the booking. Use 0 if you don’t charge extra.
              </p>
              <div class="sf-fee">
                <span aria-hidden="true">€</span>
                <ValidationProvider rules="double" v-slot="{ errors }">
                  <fg-input
                    class="mb-0"
                    v-model="model.onlinePaymentFee"
                    native-type="number"
                    :error="errors[0]"
                  />
                </ValidationProvider>
              </div>
            </label>
          </div>
        </section>
      </form>
    </ValidationObserver>
    </div>

    <transition name="sf-overlay">
      <div
        v-if="leaveOpen"
        class="sf-scrim"
        @click.self="stayOnPage"
      >
        <div
          class="sf-sheet"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sf-leave-title"
        >
          <button
            type="button"
            class="sf-sheet-handle"
            aria-label="Stay on this page"
            @click="stayOnPage"
          >
            <i />
          </button>
          <p class="sf-sheet-kicker">Shopfront</p>
          <h2 id="sf-leave-title">Unsaved changes</h2>
          <p class="sf-sheet-lead is-warn">
            Leave this page without saving? Your edits will be lost.
          </p>
          <div class="sf-sheet-actions">
            <button class="sf-sheet-quiet" type="button" @click="stayOnPage">
              Stay
            </button>
            <button class="sf-sheet-leave" type="button" @click="leavePage">
              Leave without saving
            </button>
          </div>
        </div>
      </div>
    </transition>

    <div class="sf-dock" :class="{ 'is-dirty': dirty }" role="status">
      <div class="sf-dock-inner">
        <p class="sf-dock-status">
          {{
            isSaving
              ? "Saving…"
              : dirty
              ? "Unsaved changes"
              : "Saved"
          }}
        </p>
        <div class="sf-dock-actions">
          <button
            class="sf-chip"
            type="button"
            :disabled="isSaving || !dirty"
            @click="revert"
          >
            Discard
          </button>
          <button
            class="sf-chip is-primary"
            :class="{ 'is-ready': dirty && !isSaving }"
            type="submit"
            form="shop-settings-form"
            :disabled="isSaving || !dirty"
          >
            {{ isSaving ? "Saving…" : "Save" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import _ from "lodash";

import env from "@/env";
import ShopLogoInput from "@/components/ShopLogoInput";
import VSwatches from "vue-swatches";
import { mergeShop } from "@/helpers/shopDefaults";
import {
  TIME_FORMAT_CODE,
  TIME_FORMAT_READABLE
} from "@/helpers/time";
const AnyModeOptions = [
  { value: "Off", label: "A person" },
  { value: "On", label: "A person, or anyone free" },
  { value: "Only Allow Any", label: "Anyone free only" }
];

export default {
  components: {
    ShopLogoInput,
    VSwatches
  },
  data() {
    return {
      TIME_FORMAT_CODE,
      TIME_FORMAT_READABLE,
      AnyModeOptions,
      isSaving: false,
      dirty: false,
      hydrating: false,
      copied: false,
      leaveOpen: false,
      leaveNext: null,
      model: mergeShop({}),
      days: [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday"
      ]
    };
  },
  computed: {
    ...mapGetters({
      shop: "shops/shop",
      shopId: "shops/shopId",
      userSettings: "userSettings"
    }),
    storefrontUrl() {
      const { shopId } = this;
      if (!shopId) return "";
      return `${env.bookBaseUrl}/shops/${shopId}`;
    }
  },
  watch: {
    shop: {
      immediate: true,
      deep: true,
      handler() {
        if (this.dirty || this.isSaving) return;
        this.resetModel();
      }
    },
    model: {
      deep: true,
      handler() {
        if (this.hydrating) return;
        this.dirty = true;
      }
    }
  },
  methods: {
    ...mapActions({
      updateShop: "shops/updateShop",
      uploadShopLogo: "shops/uploadShopLogo"
    }),
    dayLabel(day) {
      return _.startCase(day);
    },
    resetModel() {
      this.hydrating = true;
      this.model = mergeShop(_.cloneDeep(this.shop || {}));
      this.dirty = false;
      this.$nextTick(() => {
        this.hydrating = false;
      });
    },
    revert() {
      this.dirty = false;
      this.resetModel();
    },
    stayOnPage() {
      if (this.leaveNext) this.leaveNext(false);
      this.leaveNext = null;
      this.leaveOpen = false;
    },
    leavePage() {
      const next = this.leaveNext;
      this.leaveNext = null;
      this.leaveOpen = false;
      this.dirty = false;
      if (next) next();
    },
    onLeaveKey(event) {
      if (event.key === "Escape" && this.leaveOpen) this.stayOnPage();
    },
    onBeforeUnload(event) {
      if (!this.dirty || this.isSaving) return;
      event.preventDefault();
      event.returnValue = "";
    },
    async copyUrl() {
      if (!this.storefrontUrl) return;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(this.storefrontUrl);
        } else {
          window.prompt("Copy this URL", this.storefrontUrl);
        }
        this.copied = true;
        window.setTimeout(() => {
          this.copied = false;
        }, 1600);
      } catch (e) {
        window.prompt("Copy this URL", this.storefrontUrl);
      }
    },
    onLogoError({ message }) {
      this.$notify({
        type: "danger",
        title: this.$t("message.error"),
        text: message
      });
    },
    useBusinessDetails() {
      const company = (this.userSettings && this.userSettings.company) || {};
      this.model = {
        ...this.model,
        name: company.name || this.model.name,
        logoDownloadUrl: company.logoDownloadUrl || this.model.logoDownloadUrl,
        address: {
          ...this.model.address,
          ...(company.address || {})
        },
        contactEmail: company.emailAddress || this.model.contactEmail
      };
    },
    async submit() {
      this.isSaving = true;
      try {
        const payload = { ...this.model };
        if (
          payload.logoDownloadUrl &&
          String(payload.logoDownloadUrl).startsWith("data:")
        ) {
          payload.logoDownloadUrl = await this.uploadShopLogo(
            payload.logoDownloadUrl
          );
        }
        await this.updateShop(payload);
        this.dirty = false;
        this.resetModel();
        this.$notify({
          type: "success",
          title: this.$t("message.success"),
          text: "Shopfront settings saved."
        });
      } catch (e) {
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text: (e && e.message) || this.$t("message.somethingWentWrong")
        });
      } finally {
        this.isSaving = false;
      }
    }
  },
  created() {
    document.body.classList.add("st-surface");
    window.addEventListener("beforeunload", this.onBeforeUnload);
    window.addEventListener("keydown", this.onLeaveKey);
  },
  beforeDestroy() {
    document.body.classList.remove("st-surface");
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    window.removeEventListener("keydown", this.onLeaveKey);
    if (this.leaveNext) {
      this.leaveNext(false);
      this.leaveNext = null;
    }
  },
  beforeRouteLeave(to, from, next) {
    if (!this.dirty || this.isSaving) {
      next();
      return;
    }
    if (this.leaveOpen) {
      next(false);
      return;
    }
    this.leaveNext = next;
    this.leaveOpen = true;
  }
};
</script>
<style lang="scss" scoped>
.sf {
  --ink: #14110e;
  --ink-soft: #3c352e;
  --muted: #7a7268;
  --line: rgba(20, 17, 14, 0.1);
  --line-strong: rgba(20, 17, 14, 0.14);
  --canvas: #efe8dc;
  --surface: #fbf7f0;
  --surface-2: #fffdf8;
  --shadow: 0 1px 0 rgba(255, 255, 255, 0.65) inset,
    0 18px 40px rgba(42, 28, 14, 0.07), 0 2px 6px rgba(42, 28, 14, 0.04);
  --ease: cubic-bezier(0.22, 1, 0.36, 1);
  --font: "Instrument Sans", "Montserrat", "Helvetica Neue", sans-serif;
  --display: "Instrument Serif", "Iowan Old Style", Georgia, serif;
  --radius: 24px;
  position: relative;
  display: flex;
  flex-direction: column;
  max-width: none;
  margin: -86px -15px 0;
  height: calc(100vh - 62px);
  min-height: calc(100vh - 62px);
  max-height: calc(100vh - 62px);
  height: calc(100svh - 62px);
  min-height: calc(100svh - 62px);
  max-height: calc(100svh - 62px);
  height: calc(100dvh - 62px);
  min-height: calc(100dvh - 62px);
  max-height: calc(100dvh - 62px);
  overflow: hidden;
  color: var(--ink);
  font-family: var(--font);
  background:
    radial-gradient(900px 320px at 8% -10%, rgba(255, 255, 255, 0.7), transparent 58%),
    radial-gradient(720px 260px at 100% 0%, rgba(232, 214, 186, 0.4), transparent 52%),
    var(--canvas);
  -webkit-font-smoothing: antialiased;
}

.sf-main {
  flex: 1;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding: 18px 16px 24px;
}

.sf-progress {
  flex-shrink: 0;
  z-index: 8;
  height: 2px;
  overflow: hidden;
  background: rgba(20, 17, 14, 0.08);
}

.sf-progress i {
  display: block;
  width: 36%;
  height: 100%;
  background: var(--ink);
  animation: sf-load 1.05s ease infinite;
}

@keyframes sf-load {
  from {
    transform: translateX(-120%);
  }
  to {
    transform: translateX(380%);
  }
}

.sf-top,
.sf-body {
  width: 100%;
  max-width: 1080px;
  margin-left: auto;
  margin-right: auto;
}

.sf-top {
  display: grid;
  gap: 16px;
  margin-bottom: 18px;
}

.sf-kicker {
  margin: 0 0 6px;
  color: var(--muted);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.sf-title {
  margin: 0;
  font-family: var(--display);
  font-size: clamp(32px, 6vw, 44px);
  font-weight: 400;
  letter-spacing: -0.03em;
  line-height: 0.95;
}

.sf-sub {
  margin: 8px 0 0;
  color: var(--ink-soft);
  font-size: 15px;
  line-height: 1.45;
}

.sf-chip,
.sf-text-btn,
.sf-seg-btn {
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
  touch-action: manipulation;
}

.sf-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 16px;
  border-radius: 999px;
  background: rgba(255, 253, 248, 0.72);
  box-shadow: inset 0 0 0 1px var(--line);
  color: var(--ink);
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
}

.sf-chip:disabled {
  opacity: 0.45;
  cursor: default;
}

.sf-chip.is-primary {
  background: var(--ink);
  color: #fffdf8;
  box-shadow: none;
}

.sf-chip-sm {
  min-height: 36px;
  padding: 0 12px;
  font-size: 13px;
}

.sf-text-btn {
  flex-shrink: 0;
  color: var(--muted);
  font-size: 13px;
  font-weight: 600;
  text-align: right;
}

.sf-body {
  display: grid;
  gap: 16px;
}

.sf-card {
  padding: 20px 18px 22px;
  border-radius: var(--radius);
  background: var(--surface-2);
  box-shadow: var(--shadow);
}

.sf-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.sf-card-head h2 {
  margin: 0;
  font-family: var(--display);
  font-size: 28px;
  font-weight: 400;
  letter-spacing: -0.03em;
  color: var(--ink);
}

.sf-lead {
  margin: 6px 0 0;
  color: var(--ink-soft);
  font-size: 15px;
  font-weight: 400;
  line-height: 1.4;
}

.sf-hint {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.45;
}

.sf-hint-block {
  margin: -4px 0 0;
}

.sf-field {
  display: grid;
  gap: 6px;
  margin: 0;
  color: var(--muted);
  font-size: 13px;
  font-weight: 600;
}

.sf-identity {
  display: grid;
  gap: 20px;
}

.sf-identity-fields {
  display: grid;
  gap: 16px;
}

.sf-stack {
  display: grid;
  gap: 18px;
}

.sf-pair {
  display: grid;
  gap: 16px;
}

.sf-field-narrow {
  max-width: 280px;
}

.sf-url {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}

.sf-url > a {
  color: var(--ink);
  overflow-wrap: anywhere;
  flex: 1 1 220px;
}

.sf-url-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.sf-hours {
  display: grid;
  gap: 10px;
}

.sf-day {
  display: grid;
  gap: 10px;
  padding: 14px;
  border-radius: 18px;
  background: var(--surface);
  box-shadow: inset 0 0 0 1px var(--line);
}

.sf-day.is-closed {
  opacity: 0.72;
}

.sf-day-name {
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr);
  align-items: center;
  column-gap: 10px;
  margin: 0;
  color: var(--ink);
  font-size: 15px;
  font-weight: 600;
}

.sf-day-name input {
  width: 18px;
  height: 18px;
  margin: 0;
}

.sf-day-state {
  grid-column: 2;
  color: var(--muted);
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
}

.sf-day:not(.is-closed) .sf-day-state {
  color: #1c4631;
}

.sf-day-times {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.sf-seg {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.sf-seg-btn {
  min-height: 40px;
  padding: 0 14px;
  border-radius: 999px;
  background: var(--surface);
  box-shadow: inset 0 0 0 1px var(--line);
  font-size: 13px;
  font-weight: 600;
}

.sf-seg-btn.is-on {
  background: var(--ink);
  color: #fffdf8;
  box-shadow: none;
}

.sf-fee {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 8px;
}

.sf-fee > span {
  color: var(--ink);
  font-size: 16px;
}

.sf-dock {
  flex-shrink: 0;
  z-index: 12;
  width: 100%;
  margin: 0;
  border-radius: 0;
  background: #d9d0c2;
  box-shadow:
    0 -1px 0 rgba(20, 17, 14, 0.12),
    0 -16px 32px rgba(42, 28, 14, 0.1);
}

.sf-dock.is-dirty {
  background: #d2c8b6;
  box-shadow:
    0 -1px 0 rgba(20, 17, 14, 0.18),
    0 -18px 36px rgba(42, 28, 14, 0.12);
}

.sf-dock-inner {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  max-width: 1080px;
  margin: 0 auto;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
}

.sf-dock-status {
  flex: 1;
  min-width: 0;
  margin: 0;
  color: var(--muted);
  font-size: 13px;
  font-weight: 600;
}

.sf-dock.is-dirty .sf-dock-status {
  color: var(--ink);
}

.sf-dock-actions {
  display: flex;
  flex-shrink: 0;
  gap: 8px;
}

.sf-dock-actions .sf-chip {
  min-width: 108px;
  background: #fffdf8;
  box-shadow: inset 0 0 0 1px rgba(20, 17, 14, 0.16);
  white-space: nowrap;
  -webkit-tap-highlight-color: rgba(20, 17, 14, 0.12);
}

.sf-dock-actions .sf-chip.is-primary {
  background: var(--ink);
  color: #fffdf8;
  box-shadow: none;
}

.sf-chip.is-primary.is-ready {
  animation: sf-save-pulse 1.8s var(--ease) infinite;
}

@keyframes sf-save-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(20, 17, 14, 0.28);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(20, 17, 14, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .sf-chip.is-primary.is-ready {
    animation: none;
  }
}

.sf-scrim {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(20, 16, 12, 0.38);
  backdrop-filter: blur(10px);
}

.sf-sheet {
  width: min(520px, 100%);
  padding: 8px 18px calc(18px + env(safe-area-inset-bottom));
  border-radius: 28px 28px 0 0;
  background: var(--surface-2);
  box-shadow: 0 -24px 80px rgba(20, 17, 14, 0.22);
}

.sf-sheet-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 28px;
  margin: 0 0 8px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.sf-sheet-handle i {
  display: block;
  width: 42px;
  height: 4px;
  border-radius: 99px;
  background: #d8d0c4;
}

.sf-sheet-kicker {
  margin: 0;
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
}

.sf-sheet h2 {
  margin: 0 0 6px;
  font-family: var(--display);
  font-size: 30px;
  font-weight: 400;
  letter-spacing: -0.03em;
}

.sf-sheet-lead {
  margin: 8px 0 0;
  color: var(--ink-soft);
  font-size: 14px;
  line-height: 1.45;
}

.sf-sheet-lead.is-warn {
  color: #8a3c14;
}

.sf-sheet-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 18px;
}

.sf-sheet-quiet,
.sf-sheet-leave {
  border: 0;
  font: inherit;
  cursor: pointer;
  touch-action: manipulation;
}

.sf-sheet-quiet {
  min-height: 44px;
  padding: 0 16px;
  border-radius: 999px;
  background: rgba(255, 253, 248, 0.7);
  box-shadow: inset 0 0 0 1px var(--line);
  color: var(--ink);
  font-size: 14px;
  font-weight: 600;
}

.sf-sheet-leave {
  min-height: 44px;
  padding: 0 18px;
  border-radius: 999px;
  background: #8f2d1f;
  color: #f8efe8;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 10px 22px rgba(143, 45, 31, 0.22);
}

.sf-overlay-enter-active,
.sf-overlay-leave-active {
  transition: opacity 0.28s ease;
}

.sf-overlay-enter-active .sf-sheet,
.sf-overlay-leave-active .sf-sheet {
  transition: transform 0.38s var(--ease), opacity 0.28s ease;
}

.sf-overlay-enter,
.sf-overlay-leave-to {
  opacity: 0;
}

.sf-overlay-enter .sf-sheet,
.sf-overlay-leave-to .sf-sheet {
  transform: translateY(22px);
  opacity: 0;
}

.sf ::v-deep .form-group {
  margin-bottom: 0;
}

.sf ::v-deep .form-control {
  min-height: 44px;
  border-radius: 14px;
  background: #fffdf8;
  box-shadow: inset 0 0 0 1px var(--line);
}

.sf ::v-deep .vue-swatches {
  margin-top: 4px;
}

.sf ::v-deep .date-time-picker {
  width: 100%;
}

@media (min-width: 768px) {
  .sf-main {
    padding: 24px 24px 28px;
  }

  .sf-scrim {
    align-items: center;
    padding: 24px;
  }

  .sf-sheet {
    border-radius: 28px;
    padding: 22px 22px 20px;
    box-shadow: 0 24px 80px rgba(20, 17, 14, 0.22);
  }

  .sf-overlay-enter .sf-sheet,
  .sf-overlay-leave-to .sf-sheet {
    transform: translateY(12px) scale(0.98);
  }

  .sf-dock-inner {
    padding: 14px 24px calc(14px + env(safe-area-inset-bottom, 0px));
  }

  .sf-identity {
    grid-template-columns: 220px minmax(0, 1fr);
    align-items: start;
    gap: 32px;
  }

  .sf-pair {
    grid-template-columns: 1fr 1fr;
  }

  .sf-day {
    grid-template-columns: 150px 170px 170px minmax(0, 1fr);
    align-items: center;
    gap: 12px;
  }

  .sf-day-times {
    display: contents;
  }

  .sf-day-note {
    min-width: 0;
  }
}

@media (min-width: 1100px) {
  .sf-main {
    padding: 28px 40px 32px;
  }

  .sf-dock-inner {
    padding-left: 40px;
    padding-right: 40px;
  }
}

@media (max-width: 767px) {
  .sf-sheet-actions {
    flex-direction: column-reverse;
  }

  .sf-sheet-quiet,
  .sf-sheet-leave {
    width: 100%;
    text-align: center;
  }

  .sf-dock-inner {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 10px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  }

  .sf-dock-status {
    font-size: 12px;
  }

  .sf-dock-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    width: auto;
    min-width: 0;
    flex: none;
    gap: 10px;
  }

  .sf-dock-actions .sf-chip {
    min-width: 0;
    min-height: 48px;
    padding: 0 12px;
    font-size: 16px;
  }
}
</style>
